// components/AuthModal.js - Authentication modal for court registration
import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  InputAdornment,
  IconButton,
  Tabs,
  Tab,
  Box,
  Typography,
  Alert,
  CircularProgress
} from '@mui/material';
import {
  Visibility,
  VisibilityOff,
  SportsTennis,
  PersonAdd,
  Login
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import user_api from '../api/user';
import { useAuth } from '../contexts/AuthContext';

// Styled components
const StyledDialog = styled(Dialog)(({ theme }) => ({
  '& .MuiDialog-paper': {
    borderRadius: 16,
    minWidth: 400,
    maxWidth: 500,
  },
}));

const StyledDialogTitle = styled(DialogTitle)(({ theme }) => ({
  backgroundColor: theme.palette.primary.main,
  color: theme.palette.primary.contrastText,
  textAlign: 'center',
  padding: theme.spacing(3),
  '& .MuiSvgIcon-root': {
    marginRight: theme.spacing(1),
    verticalAlign: 'middle',
  },
}));

const TabPanel = ({ children, value, index, ...other }) => (
  <div
    role="tabpanel"
    hidden={value !== index}
    id={`auth-tabpanel-${index}`}
    aria-labelledby={`auth-tab-${index}`}
    {...other}
  >
    {value === index && <Box sx={{ pt: 2 }}>{children}</Box>}
  </div>
);

const AuthModal = ({ 
  open, 
  onClose, 
  onSuccess, 
  defaultRole = 'user',
  title = 'Authentication Required',
  subtitle = 'Please sign in or create an account to continue',
  showRoleInfo = false
}) => {
  const { login } = useAuth();
  const [tabValue, setTabValue] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showPassword, setShowPassword] = useState(false);

  // Login form state
  const [loginForm, setLoginForm] = useState({
    email: '',
    password: ''
  });

  // Register form state
  const [registerForm, setRegisterForm] = useState({
    name: '',
    email: '',
    phone: '',
    password: '',
    confirmPassword: ''
  });

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
    setError('');
  };

  const handleLoginChange = (field) => (event) => {
    setLoginForm(prev => ({
      ...prev,
      [field]: event.target.value
    }));
    setError('');
  };

  const handleRegisterChange = (field) => (event) => {
    setRegisterForm(prev => ({
      ...prev,
      [field]: event.target.value
    }));
    setError('');
  };

  const validateRegisterForm = () => {
    const { name, email, phone, password, confirmPassword } = registerForm;
    
    if (!name || !email || !phone || !password || !confirmPassword) {
      setError('All fields are required');
      return false;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setError('Please enter a valid email address');
      return false;
    }

    if (password.length < 6) {
      setError('Password must be at least 6 characters long');
      return false;
    }

    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return false;
    }

    return true;
  };

  const handleSignIn = async () => {
    if (!loginForm.email || !loginForm.password) {
      setError('All fields are required');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const result = await user_api.sign_in_email({
        email: loginForm.email,
        password: loginForm.password,
      });

      if (result.status === 200) {
        // Use AuthContext login function
        login(result.data.token, result);

        // Call success callback
        onSuccess && onSuccess(result.data);
        
        // Close modal
        onClose();
      } else {
        setError(result.data.msg || result.data.message || 'Login failed');
      }
    } catch (error) {
      console.error('Login error:', error);
      setError('Login failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleSignUp = async () => {
    if (!validateRegisterForm()) {
      return;
    }

    setLoading(true);
    setError('');

    try {
      const signupData = {
        name: registerForm.name,
        email: registerForm.email,
        phone: registerForm.phone,
        password: registerForm.password,
        role: defaultRole // This will be 'admin' for court registration
      };

      console.log('Signing up with role:', defaultRole);
      const result = await user_api.sign_up_email(signupData);

      if (result.status === 200) {
        // Auto-login after successful registration
        const loginResult = await user_api.sign_in_email({
          email: registerForm.email,
          password: registerForm.password,
        });

        if (loginResult.status === 200) {
          // Use AuthContext login function
          login(loginResult.data.token, loginResult);

          // Call success callback
          onSuccess && onSuccess(loginResult.data);
          
          // Close modal
          onClose();
        } else {
          // Registration successful but auto-login failed
          setTabValue(0); // Switch to login tab
          setLoginForm({
            email: registerForm.email,
            password: ''
          });
          setError('Registration successful! Please log in with your credentials.');
        }
      } else {
        setError(result.data.msg || result.data.message || 'Registration failed');
      }
    } catch (error) {
      console.error('Registration error:', error);
      setError('Registration failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    // Reset form states
    setLoginForm({ email: '', password: '' });
    setRegisterForm({ name: '', email: '', phone: '', password: '', confirmPassword: '' });
    setError('');
    setTabValue(0);
    onClose();
  };

  return (
    <StyledDialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <StyledDialogTitle>
        <SportsTennis />
        {title}
      </StyledDialogTitle>
      
      <DialogContent sx={{ p: 0 }}>
        {subtitle && (
          <Box sx={{ p: 3, pb: 1 }}>
            <Typography variant="body2" color="text.secondary" align="center">
              {subtitle}
            </Typography>
            {showRoleInfo && defaultRole === 'admin' && (
              <Alert severity="info" sx={{ mt: 2 }}>
                You'll be registered as a <strong>Court Administrator</strong> to manage your court listings.
              </Alert>
            )}
          </Box>
        )}

        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs 
            value={tabValue} 
            onChange={handleTabChange} 
            variant="fullWidth"
            sx={{ px: 3 }}
          >
            <Tab icon={<Login />} label="Sign In" />
            <Tab icon={<PersonAdd />} label="Sign Up" />
          </Tabs>
        </Box>

        {error && (
          <Box sx={{ p: 3, pb: 0 }}>
            <Alert severity="error">{error}</Alert>
          </Box>
        )}

        {/* Sign In Tab */}
        <TabPanel value={tabValue} index={0}>
          <Box sx={{ p: 3 }}>
            <TextField
              autoFocus
              margin="dense"
              label="Email Address"
              type="email"
              fullWidth
              variant="outlined"
              value={loginForm.email}
              onChange={handleLoginChange('email')}
              disabled={loading}
              sx={{ mb: 2 }}
            />
            <TextField
              margin="dense"
              label="Password"
              type={showPassword ? 'text' : 'password'}
              fullWidth
              variant="outlined"
              value={loginForm.password}
              onChange={handleLoginChange('password')}
              disabled={loading}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowPassword(!showPassword)}
                      edge="end"
                      disabled={loading}
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                )
              }}
            />
          </Box>
        </TabPanel>

        {/* Sign Up Tab */}
        <TabPanel value={tabValue} index={1}>
          <Box sx={{ p: 3 }}>
            <TextField
              autoFocus
              margin="dense"
              label="Full Name"
              type="text"
              fullWidth
              variant="outlined"
              value={registerForm.name}
              onChange={handleRegisterChange('name')}
              disabled={loading}
              sx={{ mb: 2 }}
            />
            <TextField
              margin="dense"
              label="Email Address"
              type="email"
              fullWidth
              variant="outlined"
              value={registerForm.email}
              onChange={handleRegisterChange('email')}
              disabled={loading}
              sx={{ mb: 2 }}
            />
            <TextField
              margin="dense"
              label="Contact Number"
              type="text"
              fullWidth
              variant="outlined"
              value={registerForm.phone}
              onChange={handleRegisterChange('phone')}
              disabled={loading}
              sx={{ mb: 2 }}
            />
            <TextField
              margin="dense"
              label="Password"
              type={showPassword ? 'text' : 'password'}
              fullWidth
              variant="outlined"
              value={registerForm.password}
              onChange={handleRegisterChange('password')}
              disabled={loading}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowPassword(!showPassword)}
                      edge="end"
                      disabled={loading}
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                )
              }}
              sx={{ mb: 2 }}
            />
            <TextField
              margin="dense"
              label="Confirm Password"
              type={showPassword ? 'text' : 'password'}
              fullWidth
              variant="outlined"
              value={registerForm.confirmPassword}
              onChange={handleRegisterChange('confirmPassword')}
              disabled={loading}
            />
          </Box>
        </TabPanel>
      </DialogContent>

      <DialogActions sx={{ p: 3, pt: 0 }}>
        <Button onClick={handleClose} disabled={loading}>
          Cancel
        </Button>
        <Button 
          onClick={tabValue === 0 ? handleSignIn : handleSignUp}
          variant="contained"
          disabled={loading}
          startIcon={loading && <CircularProgress size={20} />}
          sx={{ minWidth: 120 }}
        >
          {loading ? 'Processing...' : (tabValue === 0 ? 'Sign In' : 'Sign Up')}
        </Button>
      </DialogActions>
    </StyledDialog>
  );
};

export default AuthModal;